import { Pencil } from "lucide-react";
import { useState } from "react";

import { type TunnelRow, useUpdateOrganizationTunnel } from "@/client/backend/tunnels";
import { FormError, SubmitButton } from "@/components/network/form";
import { SelectorsInput, selectorLines } from "@/components/network/selectors-input";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

// TunnelSelectors shows a tunnel's destinations and lets an admin edit them.
// The hub sends the current ones with each attach, so an edit reaches
// instances attached after it.
export function TunnelSelectors({
	organizationId,
	tunnel,
	canEdit,
}: {
	organizationId: string;
	tunnel: TunnelRow;
	canEdit: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState<string>();
	const { trigger: update, isMutating } = useUpdateOrganizationTunnel(organizationId);

	return (
		<div className="flex max-w-80 flex-wrap items-center gap-1">
			{tunnel.selectors.map((selector) => (
				<code key={selector} className="rounded border bg-muted/40 px-1.5 py-0.5 text-xs">
					{selector}
				</code>
			))}
			{canEdit && (
				<Button
					variant="ghost"
					size="icon"
					className="size-6 text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
					onClick={() => setOpen(true)}
				>
					<Pencil className="size-3.5" />
					<span className="sr-only">Edit the selectors of {tunnel.name}</span>
				</Button>
			)}
			<Dialog
				open={open}
				onOpenChange={(next) => {
					setOpen(next);
					if (!next) setError(undefined);
				}}
			>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Selectors of {tunnel.name}</DialogTitle>
						<DialogDescription>
							Instances attached after the change get the new ones; attached instances keep theirs.
						</DialogDescription>
					</DialogHeader>
					<form
						className="space-y-4"
						onSubmit={async (event) => {
							event.preventDefault();
							const selectors = selectorLines(String(new FormData(event.currentTarget).get("selectors") ?? ""));
							if (selectors.length === 0) {
								setError("Add at least one selector.");
								return;
							}
							setError(undefined);
							const response = await update({ id: tunnel.id, selectors });
							if (!response || response.error) {
								setError(response?.error?.message ?? "The selectors could not be saved.");
								return;
							}
							setOpen(false);
						}}
					>
						<div className="space-y-2">
							<Label htmlFor={`selectors-${tunnel.id}`}>Selectors</Label>
							<SelectorsInput
								id={`selectors-${tunnel.id}`}
								rows={4}
								defaultValue={tunnel.selectors.join("\n")}
								disabled={isMutating}
							/>
						</div>
						<FormError error={error} />
						<DialogFooter>
							<SubmitButton pending={isMutating}>Save selectors</SubmitButton>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
